import {Box, Button} from "@mui/material";
import Link from "next/link";

type PaginationBarProps = {
  route: string;
  page: number;
  pageCount: number;
  searchParams?: Record<string, string | string[] | undefined>;
  windowSize?: number;
};

export function PaginationBar({
                                route,
                                page,
                                pageCount,
                                searchParams,
                                windowSize = 2
                              }: PaginationBarProps) {
  if (pageCount <= 1) return null;

  function buildHref(nextPage: number) {
    const params = new URLSearchParams();

    if (searchParams) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (key === "page") return;

        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v));
        } else if (value) {
          params.set(key, value);
        }
      });
    }

    params.set("page", String(nextPage));
    return `${route}?${params.toString()}`;
  }

  function renderPageButton(p: number) {
    if (p === page) {
      return (
          <Button
              key={p}
              variant="contained"
              disableRipple
              disableElevation
              aria-current="page"
              sx={{pointerEvents: "none", cursor: "default", fontWeight: 600}}
          >
            {p}
          </Button>
      );
    }

    return (
        <Button
            key={p}
            component={Link}
            href={buildHref(p)}
            variant="outlined"
        >
          {p}
        </Button>
    );
  }

  function renderEllipsis(key: string) {
    return (
        <Button key={key} disabled sx={{minWidth: 36}}>
          …
        </Button>
    );
  }

  const pages: (number | "ellipsis")[] = [];

  const visibleCount = windowSize * 2 + 1;

  if (pageCount <= visibleCount + 2) {
    for (let i = 1; i <= pageCount; i++) {
      pages.push(i);
    }
  } else {

    let start = page - windowSize;
    let end = page + windowSize;

    // nach links schieben wenn wir unter 2 fallen
    if (start < 2) {
      end += 2 - start;
      start = 2;
    }

    // nach rechts schieben wenn wir über pageCount-1 hinausgehen
    if (end > pageCount - 1) {
      start -= end - (pageCount - 1);
      end = pageCount - 1;
    }

    start = Math.max(2, start);
    end = Math.min(pageCount - 1, end);

    pages.push(1);

    if (start > 2) {
      pages.push("ellipsis");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < pageCount - 1) {
      pages.push("ellipsis");
    }

    pages.push(pageCount);
  }

  return (
      <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            mt: 6
          }}
      >

        {/* Zurück */}
        {page > 1 ? (
            <Button component={Link} href={buildHref(page - 1)}>
              Zurück
            </Button>
        ) : (
            <Button disabled>Zurück</Button>
        )}

        {/* Seiten */}
        {pages.map((p, index) =>
            p === "ellipsis"
                ? renderEllipsis(`ellipsis-${index}`)
                : renderPageButton(p)
        )}

        {/* Weiter */}
        {page < pageCount ? (
            <Button component={Link} href={buildHref(page + 1)}>
              Weiter
            </Button>
        ) : (
            <Button disabled>Weiter</Button>
        )}
      </Box>
  );
}
